import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import { outlineLevelFromSectionTitle } from '../../utils/protocolExportContent';
import { PdfTable, renderTextRuns } from './pdfTable';

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12, fontFamily: 'Times-Roman' },
  h1: { fontSize: 18, marginBottom: 8, fontFamily: 'Times-Bold' },
  h2: { fontSize: 16, marginBottom: 8, fontFamily: 'Times-Bold' },
  h3: { fontSize: 14, marginBottom: 6, fontFamily: 'Times-Bold' },
  p: { marginBottom: 8, textAlign: 'justify', lineHeight: 1.3 },
  listItemText: { marginLeft: 14, marginBottom: 4, textAlign: 'justify', lineHeight: 1.3 },
});

function renderBlocks(blocks) {
  return blocks.map((b, i) => {
    switch (b.type) {
      case 'heading': {
        const style = b.level === 1 ? styles.h1 : b.level === 2 ? styles.h2 : styles.h3;
        return <Text key={i} style={style}>{b.text}</Text>;
      }
      case 'paragraph': {
        const text = (b.runs || [{ text: '' }]).map((r, idx) => (
          <Text
            key={idx}
            style={{
              fontFamily: r.bold ? 'Times-Bold' : r.italic ? 'Times-Italic' : 'Times-Roman',
            }}
          >
            {r.text}
          </Text>
        ));
        return <Text key={i} style={styles.p}>{text}</Text>;
      }
      case 'list': {
        return (
          <View key={i} style={{ marginBottom: 6 }} wrap>
            {b.items.map((runs, j) => {
              const text = runs.map((r) => r.text).join('');
              const prefix = b.ordered ? `${j + 1}. ` : '• ';
              return (
                <Text key={j} wrap={false} style={styles.listItemText}>
                  {prefix}{text}
                </Text>
              );
            })}
          </View>
        );
      }
      case 'table': {
        // Same global PdfTable path as protocol export (no display:table / no chunk squash).
        return (
          <PdfTable
            key={i}
            rows={b.rows}
            renderCell={renderTextRuns}
          />
        );
      }
      default:
        return null;
    }
  });
}

function normalizeTitle(s) {
  return String(s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\.\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractNumberAndText(s) {
  const raw = String(s || '').trim();
  const m = raw.match(/^(\d+(?:\.\d+)*)\.?\s*(.*)$/);
  if (!m) return { num: null, rest: normalizeTitle(raw) };
  return { num: m[1], rest: normalizeTitle(m[2] || '') };
}

function mainSectionNumber(s) {
  const m = String(s || '').match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

const STOP_WORDS = new Set(['OF','AND','THE','TO','IN','FOR','ON','WITH','OR','BY','AT','FROM','AN','A','AS','ASSOCIATED']);
function keywords(str) {
  return normalizeTitle(str)
    .split(' ')
    .filter(w => w && !STOP_WORDS.has(w) && !/^[0-9\.]+$/.test(w));
}

function overlapScore(a, b) {
  const A = new Set(keywords(a));
  const B = new Set(keywords(b));
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  A.forEach(w => { if (B.has(w)) inter++; });
  return inter / Math.min(A.size, B.size);
}

function stripLeadingDuplicateHeading(blocks, sectionTitle) {
  if (!Array.isArray(blocks) || blocks.length === 0) return blocks;
  const { num: secNum, rest: secRestNorm } = extractNumberAndText(sectionTitle);
  const secMain = mainSectionNumber(sectionTitle);
  const isDup = (t) => {
    const { num: blkNum, rest: blkRestNorm } = extractNumberAndText(t);
    const exactNorm = normalizeTitle(t) === normalizeTitle(sectionTitle);
    const numericOnlyMatch = /^\d+(?:\.\d+)*\.?$/.test(t.trim()) && secNum && (t.replace(/\.$/, '') === secNum);
    const numberOk = (!!blkNum && !!secNum && blkNum === secNum) || !blkNum;
    const restOk = blkRestNorm === '' || blkRestNorm === secRestNorm;
    const blkMain = mainSectionNumber(t);
    const highOverlap = blkMain === secMain && overlapScore(t, sectionTitle) >= 0.5;
    const isSubsection = !!blkNum && /\./.test(blkNum) && !/^\d+(?:\.0+)?$/.test(blkNum);
    if (isSubsection) return false;
    return exactNorm || numericOnlyMatch || (numberOk && restOk) || highOverlap;
  };
  let cut = 0;
  for (let k = 0; k < Math.min(5, blocks.length); k++) {
    const blk = blocks[k];
    if (blk.type !== 'heading' && blk.type !== 'paragraph') break;
    const text = blk.type === 'heading'
      ? (blk.text || '')
      : (blk.runs || []).map(r => r.text).join('');
    if (isDup(text)) { cut++; continue; }
    break;
  }
  return cut > 0 ? blocks.slice(cut) : blocks;
}

/**
 * Ordered {level (1-4), text} heading list for the native PDF Table of Contents —
 * mirrors exactly what the body loop below renders (same printableSections filter,
 * same sectionRootPrefix, same richTextToBlocks/stripLeadingDuplicateHeading pipeline),
 * so TOC entries never drift from what's actually on the page. Page numbers aren't
 * known yet; a post-render pass locates each entry's page (see nativeProtocolPdfToc.js
 * — generic despite the name, reused here for SAP as well as Protocol).
 */
export function buildSapPdfTocEntries(sections) {
  const printableSections = Array.isArray(sections)
    ? sections.filter((s) => s && s.title && s.output && String(s.output).trim())
    : [];
  const entries = [];

  printableSections.forEach((s) => {
    const sectionLevel = Math.min(outlineLevelFromSectionTitle(s.title) + 1, 4);
    entries.push({ level: sectionLevel, text: s.title });

    // Schema sections render an image, not textual content with embedded headings.
    if (/SCHEMA/i.test(s.title || '')) return;

    const sectionRootPrefix = (String(s.title).match(/^(\d+(?:\.\d+)*)/) || [])[1] || null;
    const blocks = stripLeadingDuplicateHeading(
      richTextToBlocks(s.output, { sectionRootPrefix }),
      s.title,
    );
    blocks.forEach((b) => {
      if (b.type === 'heading') {
        entries.push({ level: Math.min(Math.max(b.level || 2, 1), 4), text: b.text });
      }
    });
  });

  return entries;
}

const SAP_TITLE_PAGE_FIELD_ROWS = [
  ['confidentialityStatement', 'Sponsor Confidentiality Statement:'],
  ['documentType', 'Document Type:'],
  ['versionNumber', 'Version Number:'],
  ['fullTitle', 'Full Title:'],
  ['trialPhase', 'Trial Phase:'],
  ['trialAcronym', 'Trial Acronym:'],
  ['shortTitle', 'Short Title:'],
  ['sponsorProtocolIdentifier', 'Sponsor Protocol Identifier:'],
  ['sponsorProductCodes', "Sponsor's Investigational Product Code(s):"],
  ['investigationalProductNames', 'Investigational Product Name(s):'],
  ['nctNumber', 'NCT No.:'],
  ['eudraCtNumber', 'EudraCT No.:'],
  ['sponsorNameAndAddress', 'Sponsor Name and Address'],
];

const SAP_PDF_DEFAULT_APPROVERS = [
  { role: 'Lead Statistician', printedName: '' },
  { role: 'Clinical Science Lead', printedName: '' },
  { role: 'Statistical Programming Lead', printedName: '' },
];

/** Title Page + Approvers and Signatories — dedicated front-matter pages built from the
 * structured SapTitlePage record, matching the SAP title-page template's two-column
 * label/value table and the Role/Printed Name/Signature/Date approvers table. `titlePage`
 * is optional — legacy (non-USDM) SAP export has no such record and falls back to a bare
 * title page, same as before this existed. */
function buildSapTitlePages(titlePage) {
  const tp = titlePage || {};
  const rows = SAP_TITLE_PAGE_FIELD_ROWS.map(([field, label]) => [
    [{ text: label, bold: true }],
    [{ text: tp[field] || '' }],
  ]);

  const approvers =
    titlePage?.approvers && titlePage.approvers.length ? titlePage.approvers : SAP_PDF_DEFAULT_APPROVERS;
  const approverRows = [
    [
      [{ text: 'Role', bold: true }],
      [{ text: 'Printed Name', bold: true }],
      [{ text: 'Signature', bold: true }],
      [{ text: 'Date', bold: true }],
    ],
    ...approvers.map((a) => [[{ text: a.role || '' }], [{ text: a.printedName || '' }], [{ text: '' }], [{ text: '' }]]),
  ];

  return [
    <Page key="title-page" size="A4" style={styles.page}>
      <Text style={[styles.h1, { textAlign: 'center', marginBottom: 20 }]}>Title Page</Text>
      <PdfTable rows={rows} renderCell={renderTextRuns} />
    </Page>,
    <Page key="approvers-page" size="A4" style={styles.page}>
      <Text style={styles.h1}>Approvers and Signatories</Text>
      <PdfTable rows={approverRows} renderCell={renderTextRuns} />
    </Page>,
  ];
}

const SAPPdfDocument = ({ sections, trialSchemaImageUrl, titlePage }) => {
  const printableSections = Array.isArray(sections)
    ? sections.filter(s => s && s.title && s.output && String(s.output).trim())
    : [];

  const frontMatterPages = titlePage
    ? buildSapTitlePages(titlePage)
    : (
      <Page size="A4" style={styles.page}>
        <Text style={{ fontSize: 22, marginBottom: 20 }}>Statistical Analysis Plan (SAP)</Text>
      </Page>
    );

  const otherPages = [];
  for (let i = 0; i < printableSections.length; i++) {
    const s = printableSections[i];
    const isSchemaSection = /SCHEMA/i.test(s.title || '');
    const sectionRootPrefix = (String(s.title).match(/^(\d+(?:\.\d+)*)/) || [])[1] || null;
    otherPages.push(
      <Page key={s._id || s.title} size="A4" style={styles.page}>
        <Text style={styles.h1}>{s.title}</Text>
        {isSchemaSection && trialSchemaImageUrl ? (
          <Image
            src={trialSchemaImageUrl}
            style={{ width: '100%', height: 320, objectFit: 'contain', marginBottom: 12 }}
          />
        ) : (
          renderBlocks(stripLeadingDuplicateHeading(richTextToBlocks(s.output, { sectionRootPrefix }), s.title))
        )}
      </Page>
    );
  }

  return (
    <Document>
      {frontMatterPages}
      {otherPages}
    </Document>
  );
};

export default SAPPdfDocument;

